import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";

// ----------------------------------------------------------------------------
// Resource listing card — Case Study + Sample Report.
//
// Mirrors BlogCard's modernised design language:
//   - rounded-xl + soft border that warms on hover (border-primary-200)
//   - subtle lift on hover (-translate-y-0.5 + low-opacity shadow)
//   - image zoom on hover (group-hover:scale-[1.04])
//   - category chip top-left of the image (backdrop-blur pill)
//   - bricolage title with clamp() sizing
//   - body text de-emphasised so the title dominates
//   - "View →" / "Download →" link with arrow slide on hover
// ----------------------------------------------------------------------------

export type IResourceListingCardKind = "case-study" | "sample-report";

const KIND_META: Record<
  IResourceListingCardKind,
  { chipLabel: string; chipClass: string; ctaLabel: string }
> = {
  "case-study": {
    chipLabel: "Case Study",
    chipClass: "bg-primary-900/95 text-white",
    ctaLabel: "View case study",
  },
  "sample-report": {
    chipLabel: "Sample Report",
    chipClass: "bg-yellow-400 text-black-900",
    ctaLabel: "View sample",
  },
};

export type IResourceListingCardProps = HTMLAttributes<HTMLDivElement> & {
  kind: IResourceListingCardKind;
  heading?: string;
  description?: ReactNode;
  link: string;
  image?: { src: string; alt: string };
  /** Optional tag rendered in the meta row above the heading
   *  (e.g. "BFSI · 2024" or "Penetration Test"). */
  tag?: string;
};

const ResourceListingCard: FC<IResourceListingCardProps> = (props) => {
  const {
    kind,
    heading,
    description,
    link,
    image,
    tag,
    className,
    ...other
  } = props;

  const meta = KIND_META[kind];

  return (
    <Link
      href={link}
      className={clsx(
        "group relative flex flex-col overflow-hidden rounded-xl border border-black-100 bg-white transition-all duration-300",
        "hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-[0_8px_24px_-12px_rgba(31,42,73,0.18)]",
        className,
      )}
      {...(other as HTMLAttributes<HTMLAnchorElement>)}
    >
      {/* ---------- Hero image ---------- */}
      <figure className="relative aspect-video w-full overflow-hidden bg-black-50">
        <span
          className={clsx(
            "absolute left-3 top-3 z-10 rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wide backdrop-blur-sm",
            meta.chipClass,
          )}
        >
          {meta.chipLabel}
        </span>

        {image?.src ? (
          <Image
            src={image.src}
            alt={image.alt}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            unoptimized
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm font-normal text-black-400">
            No image
          </div>
        )}
      </figure>

      {/* ---------- Content ---------- */}
      <div className="flex flex-1 flex-col gap-2 p-4 sm:p-4 lg:p-5">
        {tag && (
          <div className="font-mono text-[10px] font-normal uppercase tracking-[0.08em] text-black-500">
            {tag}
          </div>
        )}

        {heading && (
          <h3 className="line-clamp-3 font-bricolage-grotesque text-[clamp(1.125rem,0.5vw+0.9375rem,1.5rem)] font-medium !leading-[1.2] text-black-900 transition-colors duration-300 group-hover:text-primary-900">
            {heading}
          </h3>
        )}

        {description && (
          <div
            className={clsx(
              "line-clamp-2 text-[12px] leading-relaxed text-black-600",
              "[&_*]:!text-inherit [&_a]:pointer-events-none [&_p]:m-0",
            )}
          >
            {description}
          </div>
        )}

        {/* Spacer keeps CTA at the bottom regardless of body height */}
        <div className="grow" />

        <div className="mt-2">
          <span
            id={`${kind}-${link.split("/").filter(Boolean).pop() ?? ""}-cta`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-900 transition-colors duration-200 group-hover:text-primary-800"
          >
            {meta.ctaLabel}
            <span
              aria-hidden="true"
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            >
              →
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ResourceListingCard;
